"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.deriveMCPServerNameFromUrl = deriveMCPServerNameFromUrl;
exports.createNewActionPluginManifest = createNewActionPluginManifest;
exports.addExistingPlugin = addExistingPlugin;
exports.validateSourcePluginManifest = validateSourcePluginManifest;
exports.getODSPItemInfo = getODSPItemInfo;
exports.getGraphConnectors = getGraphConnectors;
exports.generateForMCPForDA = generateForMCPForDA;
const tslib_1 = require("tslib");
// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.
const teamsfx_api_1 = require("@microsoft/teamsfx-api");
const CopilotGptManifestUtils_1 = require("../../driver/teamsApp/utils/CopilotGptManifestUtils");
const PluginManifestUtils_1 = require("../../driver/teamsApp/utils/PluginManifestUtils");
const path_1 = tslib_1.__importDefault(require("path"));
const fs_extra_1 = tslib_1.__importDefault(require("fs-extra"));
const utils_1 = require("../../driver/teamsApp/utils/utils");
const localizeUtils_1 = require("../../../common/localizeUtils");
const common_1 = require("../../utils/common");
const telemetry_1 = require("../../../common/telemetry");
const error_1 = require("../../../error");
const axios_1 = tslib_1.__importStar(require("axios"));
const constants_1 = require("../../../common/constants");
const oneDriveSharePointHandler_1 = require("./oneDriveSharePointHandler");
const globalVars_1 = require("../../../common/globalVars");
const questionNames_1 = require("../../../question/questionNames");
const mcpToolFetcher_1 = require("../../utils/mcpToolFetcher");
const actionInjector_1 = require("../../configManager/actionInjector");
const pathUtils_1 = require("../../utils/pathUtils");
// Non-translatable CLI command template used in warning messages
const mcpAddActionHint = "atk add action --api-plugin-type mcp --mcp-da-server-url <server-url> --mcp-tools-file-path <path-to-tools-json> --interactive false";
const logMessageKeys = {
    failValidateOneDriveSharePointItem: "core.createProjectQuestion.log.fail.validateOneDriveSharePointItem",
    invalidOneDriveSharePointURL: "core.createProjectQuestion.log.fail.invalidOneDriveSharePointURL",
};
/**
 * Derives a server entry name for an MCP server from its URL. Used by both the
 * "DA with MCP" scaffolding (to render `.vscode/mcp.json.tpl`) and the
 * "Add Action with MCP" flow (to append a new entry into `.vscode/mcp.json`).
 *
 * Strips non-alphanumeric characters from the host and truncates to 10 chars.
 * Falls back to `"mcpServer"` when the URL is missing or invalid.
 */
function deriveMCPServerNameFromUrl(mcpServerUrl) {
    const fallback = "mcpServer";
    if (!mcpServerUrl) {
        return fallback;
    }
    try {
        const host = new URL(mcpServerUrl).host.replace(/[^a-zA-Z0-9]/g, "").substring(0, 10);
        return host || fallback;
    }
    catch {
        return fallback;
    }
}
/**
 * Creates a brand-new empty action plugin manifest (`ai-plugin.json`) under the
 * project's `appPackage` folder and registers it as a new action in the
 * declarative agent manifest.
 *
 * Used by the "Add MCP Action" flow when the user opts to create a new
 * action manifest rather than reuse an existing one.
 *
 * @param projectPath Absolute project root.
 * @param desiredFileName File name (no path) for the new plugin manifest, e.g. "ai-plugin.json".
 *                       If a file with this name already exists, an incrementing suffix is added.
 * @param declarativeAgentManifestPath Absolute path to the declarative agent manifest file
 *                       (e.g. `appPackage/declarativeAgent.json`).
 * @returns The absolute path to the newly-created plugin manifest and the action id used to register it.
 */
async function createNewActionPluginManifest(projectPath, desiredFileName, declarativeAgentManifestPath) {
    const appPackageFolder = path_1.default.join(projectPath, teamsfx_api_1.AppPackageFolderName);
    const fileName = desiredFileName?.trim() || teamsfx_api_1.DefaultPluginManifestFileName;
    const destinationPluginManifestPath = await CopilotGptManifestUtils_1.copilotGptManifestUtils.getDefaultNextAvailablePluginManifestPath(appPackageFolder, fileName);
    const projectName = path_1.default.basename(projectPath);
    const namespace = projectName.toLowerCase().replace(/[^a-z0-9]/g, "") || "actions";
    const skeleton = {
        $schema: "https://developer.microsoft.com/json-schemas/copilot/plugin/v2.4/schema.json",
        schema_version: "v2.4",
        name_for_human: projectName,
        description_for_human: projectName,
        namespace,
        functions: [],
        runtimes: [],
    };
    await fs_extra_1.default.ensureFile(destinationPluginManifestPath);
    await fs_extra_1.default.writeJSON(destinationPluginManifestPath, skeleton, { spaces: 4 });
    const actionId = path_1.default.basename(destinationPluginManifestPath, path_1.default.extname(destinationPluginManifestPath));
    const relativePluginPath = (0, utils_1.normalizePath)(path_1.default.relative(path_1.default.dirname(declarativeAgentManifestPath), destinationPluginManifestPath), true);
    const addActionRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.addAction(declarativeAgentManifestPath, actionId, relativePluginPath);
    if (addActionRes.isErr()) {
        return (0, teamsfx_api_1.err)(addActionRes.error);
    }
    return (0, teamsfx_api_1.ok)({ pluginManifestPath: destinationPluginManifestPath, actionId });
}
const pluginManifestPlaceholderWarning = "add-exsiting-plugin-manifest-placehoder";
const apiSpecPlaceholderWarning = "add-exsiting-plugin-api-spec-placehoder";
const readApiSpecErrorTelemetry = "read-api-spec-error";
async function addExistingPlugin(declarativeCopilotManifestPath, fromPluginManifestPath, fromApiSpecPath, actionId, context, source) {
    const pluginManifestRes = await PluginManifestUtils_1.pluginManifestUtils.readPluginManifestFile(fromPluginManifestPath);
    if (pluginManifestRes.isErr()) {
        return (0, teamsfx_api_1.err)(pluginManifestRes.error);
    }
    const pluginManifest = pluginManifestRes.value;
    // prerequiste check
    const checkRes = validateSourcePluginManifest(pluginManifest, source);
    if (checkRes.isErr()) {
        return (0, teamsfx_api_1.err)(checkRes.error);
    }
    const runtimes = pluginManifest.runtimes; // have validated that the value exists.
    const destinationApiSpecRelativePath = runtimes.find((runtime) => runtime.type === "OpenApi")
        .spec.url; // have validated that the value exists.
    const outputFolder = path_1.default.dirname(declarativeCopilotManifestPath);
    // Copy OpenAPI spec
    const originalDestApiSPecRelativePath = path_1.default.resolve(outputFolder, destinationApiSpecRelativePath);
    let destinationApiSpecPath = originalDestApiSPecRelativePath;
    const needUpdatePluginManifest = (await fs_extra_1.default.pathExists(originalDestApiSPecRelativePath)) ||
        path_1.default.relative(outputFolder, originalDestApiSPecRelativePath).startsWith("..");
    if (needUpdatePluginManifest) {
        destinationApiSpecPath = await PluginManifestUtils_1.pluginManifestUtils.getDefaultNextAvailableApiSpecPath(fromApiSpecPath, path_1.default.join(outputFolder, teamsfx_api_1.DefaultApiSpecFolderName));
    }
    await fs_extra_1.default.ensureFile(destinationApiSpecPath);
    await fs_extra_1.default.copyFile(fromApiSpecPath, destinationApiSpecPath);
    // Save plugin manifest
    if (needUpdatePluginManifest) {
        const runtimeSpecUrl = (0, utils_1.normalizePath)(path_1.default.relative(outputFolder, destinationApiSpecPath), true);
        for (const runtime of runtimes) {
            if (runtime.type === "OpenApi" && runtime.spec?.url) {
                runtime.spec.url = runtimeSpecUrl;
            }
        }
    }
    const destinationPluginManifestPath = await CopilotGptManifestUtils_1.copilotGptManifestUtils.getDefaultNextAvailablePluginManifestPath(outputFolder);
    await fs_extra_1.default.ensureFile(destinationPluginManifestPath);
    const pluginManifestContent = JSON.stringify(pluginManifest, undefined, 4);
    await fs_extra_1.default.writeFile(destinationPluginManifestPath, pluginManifestContent);
    // Update declarative copilot plugin manifest
    const addActionRes = await CopilotGptManifestUtils_1.copilotGptManifestUtils.addAction(declarativeCopilotManifestPath, actionId, (0, utils_1.normalizePath)(path_1.default.relative(outputFolder, destinationPluginManifestPath), true));
    if (addActionRes.isErr()) {
        return (0, teamsfx_api_1.err)(addActionRes.error);
    }
    const warnings = [];
    const pluginManifestVariables = (0, common_1.getEnvironmentVariables)(JSON.stringify(pluginManifest));
    if (pluginManifestVariables.length > 0) {
        warnings.push({
            type: pluginManifestPlaceholderWarning,
            content: (0, localizeUtils_1.getLocalizedString)("core.addPlugin.warning.manifestVariables", pluginManifestVariables.join(", ")),
        });
    }
    try {
        const apiSpecContent = await fs_extra_1.default.readFile(destinationApiSpecPath, "utf8");
        const apiSpecVariables = (0, common_1.getEnvironmentVariables)(apiSpecContent);
        if (apiSpecVariables.length > 0) {
            warnings.push({
                type: apiSpecPlaceholderWarning,
                content: (0, localizeUtils_1.getLocalizedString)("core.addPlugin.warning.apiSpecVariables", apiSpecVariables.join(", ")),
            });
        }
    }
    catch (e) {
        (0, telemetry_1.sendTelemetryErrorEvent)(source, readApiSpecErrorTelemetry, (0, error_1.assembleError)(e));
    }
    return (0, teamsfx_api_1.ok)({
        destinationPluginManifestPath,
        warnings,
    });
}
function validateSourcePluginManifest(manifest, source) {
    if (!manifest.schema_version) {
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(source, "MissingSchemaVersion", (0, localizeUtils_1.getDefaultString)("core.createProjectQuestion.addPlugin.MissingRequiredProperty", "schema_version"), (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.addPlugin.MissingRequiredProperty", "schema_version")));
    }
    if (!manifest.runtimes) {
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(source, "MissingRuntimes", (0, localizeUtils_1.getDefaultString)("core.createProjectQuestion.addPlugin.MissingRequiredProperty", "runtimes"), (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.addPlugin.MissingRequiredProperty", "runtimes")));
    }
    const apiSpecPaths = new Set();
    for (const runtime of manifest.runtimes) {
        if (runtime.type === "OpenApi" && runtime.spec?.url) {
            apiSpecPaths.add(runtime.spec.url);
        }
    }
    if (apiSpecPaths.size === 0) {
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(source, "MissingApiSpec", (0, localizeUtils_1.getDefaultString)("core.createProjectQuestion.addPlugin.pluginManifestMissingApiSpec", "OpenApi"), (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.addPlugin.pluginManifestMissingApiSpec", "OpenApi")));
    }
    if (apiSpecPaths.size > 1) {
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError(source, "MultipleApiSpecInPluginManifest", (0, localizeUtils_1.getDefaultString)("core.createProjectQuestion.addPlugin.pluginManifestMultipleApiSpec", Array.from(apiSpecPaths).join(", ")), (0, localizeUtils_1.getLocalizedString)("core.createProjectQuestion.addPlugin.pluginManifestMultipleApiSpec", Array.from(apiSpecPaths).join(", "))));
    }
    return (0, teamsfx_api_1.ok)(undefined);
}
async function getODSPItemInfo(context, itemUrl) {
    if (!itemUrl) {
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError("validateOneDriveSharePointItem", "InvalidInput", "Item URL is required"));
    }
    try {
        const graphClientResult = await (0, oneDriveSharePointHandler_1.createGraphClientWithToken)(context);
        if (graphClientResult.isErr()) {
            return (0, teamsfx_api_1.err)(graphClientResult.error);
        }
        const graphClient = graphClientResult.value;
        const siteResult = await (0, oneDriveSharePointHandler_1.getSharePointSiteByRelativePath)(graphClient, itemUrl);
        if (siteResult.isOk()) {
            const site = siteResult.value;
            return (0, teamsfx_api_1.ok)([
                {
                    id: site.id,
                    name: site.name,
                    webId: site.webId,
                    siteId: site.siteId,
                },
            ]);
        }
        const encodedUrl = (0, oneDriveSharePointHandler_1.encodeSharePointUrl)(itemUrl);
        const driveItem = await (0, oneDriveSharePointHandler_1.getDriveItemInfo)(graphClient, encodedUrl);
        return (0, teamsfx_api_1.ok)([
            {
                id: driveItem.id,
                name: driveItem.name,
                uniqueId: driveItem.uniqueId,
                listId: driveItem.listId,
                webId: driveItem.webId,
                siteId: driveItem.siteId,
                itemType: driveItem.itemType,
            },
        ]);
    }
    catch (error) {
        if ((0, axios_1.isAxiosError)(error) && error.response) {
            if (error.response.status >= 400 && error.response.status < 510) {
                context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(logMessageKeys.failValidateOneDriveSharePointItem, error.message));
                return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError("ValidateOneDriveSharePointURL", "GraphApiError", error.message, error.message));
            }
        }
        const message = JSON.stringify(error);
        context.logProvider?.error((0, localizeUtils_1.getLocalizedString)(logMessageKeys.failValidateOneDriveSharePointItem, message));
        return (0, teamsfx_api_1.err)(new teamsfx_api_1.SystemError("ValidateOneDriveSharePointURL", "GraphApiError", message, message));
    }
}
async function getGraphConnectors() {
    const context = (0, globalVars_1.createContext)();
    const graphTokenRes = await context.tokenProvider.m365TokenProvider.getAccessToken({
        scopes: constants_1.GCScopes,
    });
    if (graphTokenRes.isErr()) {
        throw graphTokenRes.error;
    }
    const graphToken = graphTokenRes.value;
    const instance = axios_1.default.create({
        baseURL: `${(0, constants_1.getResourceServiceEndpoint)(constants_1.ResourceServiceType.Graph)}/v1.0`,
        headers: { Authorization: `Bearer ${graphToken}` },
    });
    try {
        const res = await instance.get(`/external/connections`);
        const data = res.data;
        const result = data.value.map((item) => {
            return { id: item.id, label: item.id };
        });
        return result;
    }
    catch (error) {
        if (error.response?.status === 403) {
            const err = new teamsfx_api_1.UserError("getCopilotConnectors", "GraphApiError", (0, localizeUtils_1.getDefaultString)("core.GCList.insufficientPermission"), (0, localizeUtils_1.getDefaultString)("core.GCList.insufficientPermission"));
            throw err;
        }
        else {
            const message = `Failed to get Copilot connector item: ${error instanceof Error ? error.message : String(error)}`;
            const err = new teamsfx_api_1.UserError("copilotConnectors", "GraphApiError", message, message);
            throw err;
        }
    }
}
async function generateForMCPForDA(destinationPath, inputs) {
    // 1. Get ai-plugin.json
    const aiPluginFilePath = path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, teamsfx_api_1.DefaultPluginManifestFileName);
    if (!(await fs_extra_1.default.pathExists(aiPluginFilePath))) {
        const error = new teamsfx_api_1.SystemError("MCPForDAPluginManifestNotFound", "PluginManifestNotFound", (0, localizeUtils_1.getDefaultString)("core.MCPForDA.pluginManifestNotFound", aiPluginFilePath), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.pluginManifestNotFound", aiPluginFilePath));
        return (0, teamsfx_api_1.err)(error);
    }
    const mcpServerUrl = inputs[questionNames_1.QuestionNames.MCPForDAServerUrl];
    const serverName = inputs[questionNames_1.QuestionNames.MCPForDAServerName];
    const warnings = [];
    // If a tools file is provided (CLI flow), load tools from it
    const toolsFilePath = inputs[questionNames_1.QuestionNames.MCPToolsFilePath];
    const existingTools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
    if (toolsFilePath && (!existingTools || existingTools.length === 0)) {
        try {
            const fileTools = await (0, mcpToolFetcher_1.readMCPToolsFromFile)(toolsFilePath);
            inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = fileTools;
            if (!inputs[questionNames_1.QuestionNames.MCPForDAPreFetchTools]) {
                inputs[questionNames_1.QuestionNames.MCPForDAPreFetchTools] = fileTools.map((t) => t.name);
            }
        }
        catch {
            warnings.push({
                type: "mcpToolsFileReadError",
                content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.toolsFileReadError", toolsFilePath, mcpAddActionHint),
            });
        }
    }
    // Probe auth if tools were loaded from file but auth not yet detected
    if (inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools]?.length > 0 &&
        !inputs[questionNames_1.QuestionNames.MCPForDAAuth] &&
        mcpServerUrl) {
        try {
            const authProbe = await (0, mcpToolFetcher_1.probeMCPServerAuth)(mcpServerUrl);
            if (authProbe.requiresAuth) {
                inputs[questionNames_1.QuestionNames.MCPForDAAuth] = "OAuthPluginVault";
                if (authProbe.authMetadataUrl) {
                    inputs[questionNames_1.QuestionNames.MCPForDAAuthMetadataUrl] = authProbe.authMetadataUrl;
                }
            }
        }
        catch {
            // Auth probe failed — continue without auth
        }
    }
    // Auto-fetch tools from MCP server if no tools loaded yet
    const currentTools = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
    if ((!currentTools || currentTools.length === 0) && mcpServerUrl) {
        try {
            const result = await (0, mcpToolFetcher_1.fetchMCPTools)(mcpServerUrl);
            if (!result.requiresAuth && result.tools.length > 0) {
                inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools] = result.tools;
                if (!inputs[questionNames_1.QuestionNames.MCPForDAPreFetchTools]) {
                    inputs[questionNames_1.QuestionNames.MCPForDAPreFetchTools] = result.tools.map((t) => t.name);
                }
            }
            else if (result.requiresAuth) {
                // Store auth metadata for later use
                inputs[questionNames_1.QuestionNames.MCPForDAAuth] = "OAuthPluginVault";
                if (result.authMetadataUrl) {
                    inputs[questionNames_1.QuestionNames.MCPForDAAuthMetadataUrl] = result.authMetadataUrl;
                }
                warnings.push({
                    type: "mcpAuthRequired",
                    content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.authRequired", mcpServerUrl, mcpAddActionHint),
                });
            }
            else {
                warnings.push({
                    type: "mcpNoToolsFetched",
                    content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.noToolsFetched", mcpServerUrl, mcpAddActionHint),
                });
            }
        }
        catch {
            warnings.push({
                type: "mcpFetchError",
                content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.fetchError", mcpServerUrl, mcpAddActionHint),
            });
        }
    }
    // 2. Read ai-plugin.json
    const aiPluginContent = await fs_extra_1.default.readJSON(aiPluginFilePath);
    const mcpToolsDetail = inputs[questionNames_1.QuestionNames.MCPForDAAvailableTools];
    const mcpToolsSelected = inputs[questionNames_1.QuestionNames.MCPForDAPreFetchTools];
    if (mcpToolsDetail &&
        mcpToolsDetail.length > 0 &&
        mcpToolsSelected &&
        mcpToolsSelected.length > 0) {
        const mcpAuth = inputs[questionNames_1.QuestionNames.MCPForDAAuth];
        // Process tools: if serverName exists (VS Code flow), strip the prefix; otherwise use as-is (CLI flow)
        let processedTools;
        if (serverName) {
            processedTools = mcpToolsDetail
                .filter((tool) => tool.name.includes(serverName))
                .map((tool) => {
                const index = tool.name.indexOf(serverName);
                const newName = tool.name.substring(index + serverName.length + 1);
                return {
                    ...tool,
                    name: newName,
                };
            })
                .filter((tool) => mcpToolsSelected.includes(tool.name));
        }
        else {
            processedTools = mcpToolsDetail.filter((tool) => mcpToolsSelected.includes(tool.name));
        }
        aiPluginContent.functions = processedTools.map((tool) => {
            return {
                name: tool.name,
                description: tool.description || "",
            };
        });
        // Write mcp-tools-1.json with full tool definitions (no field filtering)
        const mcpToolsFileName = "mcp-tools-1.json";
        const mcpToolsOutputPath = path_1.default.join(destinationPath, teamsfx_api_1.AppPackageFolderName, mcpToolsFileName);
        await fs_extra_1.default.writeJSON(mcpToolsOutputPath, { tools: processedTools }, { spaces: 4 });
        const runtimeSpec = {
            url: mcpServerUrl,
            mcp_tool_description: {
                file: mcpToolsFileName,
            },
        };
        aiPluginContent.runtimes = [
            {
                type: "RemoteMCPServer",
                spec: runtimeSpec,
                run_for_functions: aiPluginContent.functions.map((func) => func.name),
            },
        ];
        // Auth handling
        if (mcpAuth === "OAuthPluginVault") {
            const authType = inputs[questionNames_1.QuestionNames.MCPForDAAuthType];
            if (!authType) {
                return (0, teamsfx_api_1.err)(new teamsfx_api_1.UserError("MCPForDA", "MissingMCPAuthType", (0, localizeUtils_1.getDefaultString)("core.MCPForDA.missingAuthType"), (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.missingAuthType")));
            }
            const registrationId = "MCP_DA_AUTH_ID_ACTION_1";
            aiPluginContent.runtimes[0].auth = {
                type: "OAuthPluginVault",
                reference_id: `$\{\{${registrationId}\}\}`,
            };
            // Resolve OAuth metadata and inject oauth/register into m365agents.yml
            try {
                let authorizationUrl;
                let tokenUrl;
                let refreshUrl;
                if (authType === "oauth") {
                    const metadata = await (0, mcpToolFetcher_1.resolveMCPOAuthMetadata)(inputs[questionNames_1.QuestionNames.MCPForDAAuthMetadataUrl], inputs[questionNames_1.QuestionNames.MCPForDAAuthWellKnownUrl]);
                    authorizationUrl = metadata.authorizationUrl;
                    tokenUrl = metadata.tokenUrl;
                    refreshUrl = metadata.refreshUrl;
                }
                const ymlPath = pathUtils_1.pathUtils.getYmlFilePath(destinationPath);
                if (ymlPath) {
                    await actionInjector_1.ActionInjector.injectCreateOAuthActionForMCP(ymlPath, authType, "action_1", registrationId, mcpServerUrl, authorizationUrl, tokenUrl, refreshUrl);
                }
            }
            catch (error) {
                warnings.push({
                    type: "mcpAuthMetadataError",
                    content: (0, localizeUtils_1.getLocalizedString)("core.MCPForDA.mcpAuthMetadataMissingError", error.message),
                });
            }
        }
    }
    // If no tools available, leave ai-plugin.json with empty functions/runtimes (template default)
    // 3. Write ai-plugin.json
    await fs_extra_1.default.writeJSON(aiPluginFilePath, aiPluginContent, { spaces: 4 });
    return (0, teamsfx_api_1.ok)({ warnings });
}
//# sourceMappingURL=helper.js.map